import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GroupIcon from '@mui/icons-material/Group';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SplashScreen from './SplashScreen';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import PoolIcon from '@mui/icons-material/Pool';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LoadingScreen from './LoadingScreen';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BoyIcon from '@mui/icons-material/Boy';
import GirlIcon from '@mui/icons-material/Girl';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import Diversity3Icon from '@mui/icons-material/Diversity3';

// Sport icon mapping
const sportIcons = {
  Football: <span style={{ color: 'white' }}><SportsSoccerIcon sx={{ fontSize: 30 }} /></span>,
  Basketball: <span style={{ color: 'white' }}><SportsBasketballIcon sx={{ fontSize: 30 }} /></span>,
  Volleyball: <span style={{ color: 'white', fontSize: 40 }}>🏐</span>,
  Athletics: <span style={{ color: 'white', fontSize: 40 }}><DirectionsRunIcon sx={{ fontSize: 30 }} /></span>,
  Judo: <span style={{ color: 'white', fontSize: 40 }}>🥋</span>,
  Badminton: <span style={{ color: 'white', fontSize: 40 }}>🏸</span>,
  Swimming: <span style={{ color: 'white', fontSize: 40 }}><PoolIcon sx={{ fontSize: 40 }} /></span>,
  Boxing: <span style={{ color: 'white', fontSize: 40 }}>🥊</span>,
  'Volet Jeunesse': <span style={{ color: 'white', fontSize: 40 }}><Diversity3Icon sx={{ fontSize: 40 }} /></span>,
  Sailing: <span style={{ color: 'white', fontSize: 40 }}>⛵</span>,
  Weightlifting: <span style={{ color: 'white', fontSize: 40 }}>🏋️</span>,
};

const translatedNames = {
  Football: 'Football',
  Basketball: 'Basket-ball',
  Volleyball: 'Volley-ball',
  Athletics: 'Athlétisme',
  Judo: 'Judo',
  Badminton: 'Badminton',
  Swimming: 'Natation',
  Boxing: 'Boxe',
  'Volet Jeunesse': 'Volet Jeunesse',
  Sailing: 'Voile',
  Weightlifting: 'Haltérophilie',
};

const SportDetails = () => {
  const { sportId } = useParams();
  const [sportName, setSportName] = useState('');
  const [sportType, setSportType] = useState('');
  const [fixtures, setFixtures] = useState([]);
  const [results, setResults] = useState([]);
  const [leaderboards, setLeaderboards] = useState({ score: {}, medals: {} });
  const [pools, setPools] = useState({ 'Pool A': [], 'Pool B': [] });
  const [poolStats, setPoolStats] = useState({ 'Pool A': [], 'Pool B': [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [participantsData, setParticipantsData] = useState([]);

  const computeCompetitors = (fixture, countriesData, participantsData) => {
    const { type, team1, team2, athletes, isHeadToHead, title } = fixture;
    if (type === 'Cultural') {
      return title || 'Cultural Event';
    } else if (type === 'Team') {
      const team1Name = team1 === 'TBC' ? 'TBC' : countriesData.find(c => c.id === team1)?.name;
      const team2Name = team2 === 'TBC' ? 'TBC' : countriesData.find(c => c.id === team2)?.name;
      if (!team1Name || !team2Name) {
        console.warn('Invalid team data:', { team1, team2, team1Name, team2Name });
        return 'Invalid teams';
      }
      return `${team1Name} vs ${team2Name}`;
    } else if (type === 'Individual') {
      const athleteNames = athletes
        .map(id => id === 'TBC' ? 'TBC' : participantsData.find(p => p.id === id)?.name)
        .filter(name => name);
      if (athleteNames.length === 0) {
        console.warn('No valid athletes for fixture');
        return 'No athletes selected';
      }
      return athleteNames.join(isHeadToHead ? ' vs ' : ', ');
    }
    console.warn('Invalid fixture type:', { type });
    return 'Invalid fixture';
  };

  const compareScores = (scoreA, scoreB, resultType) => {
    if (!scoreA || !scoreB) return !scoreA ? 1 : -1;
    if (resultType === 'Time') {
      const parseTime = (time) => {
        time = time.replace('s', '');
        if (time.includes(':')) {
          const [minutes, seconds] = time.split(':').map(parseFloat);
          return minutes * 60 + seconds;
        }
        return parseFloat(time);
      };
      return parseTime(scoreA) - parseTime(scoreB); // Lower time is better
    } else if (resultType === 'Distance' || resultType === 'Weight') {
      return parseFloat(scoreB) - parseFloat(scoreA); // Higher distance/weight is better
    }
    return 0;
  };

  const computeLeaderboard = (fixtures, countriesData, participantsData, title, category, rankData) => {
    const filteredFixtures = fixtures.filter(f => f.title === title && f.category === category);
    if (filteredFixtures.length === 0) return [];

    const sportType = filteredFixtures[0]?.type || 'Individual';
    const isBadminton = sportName.toLowerCase() === 'badminton' || title.toLowerCase().includes('badminton');
    const isFootball = title.toLowerCase().includes('football');
    const isRelay = title.toLowerCase().includes('relay') && filteredFixtures.some(f => !f.isHeadToHead);
    const isWeightlifting = sportName.toLowerCase() === 'weightlifting';

    // Determine result type based on actual scores or sport name
    const resultType = isWeightlifting ? 'Weight' :
      filteredFixtures.some(f => 
        f.score && Object.values(f.score).some(s => s && s.match(/^\d+(\.\d{1,2})?s?$|^(\d+:)?\d+\.\d{1,2}$/))
      ) ? 'Time' :
      filteredFixtures.some(f => 
        f.score && Object.values(f.score).some(s => s && s.match(/^\d+(\.\d{1,2})?$/))
      ) ? 'Distance' : 'Time';

    // Handle Badminton specifically
    if (isBadminton) {
      const athleteStats = {};
      
      filteredFixtures.forEach(fixture => {
        if (!fixture.athletes || fixture.athletes.length < 2) return;
        
        const athlete1 = fixture.athletes[0];
        const athlete2 = fixture.athletes[1];
        
        const athlete1Data = participantsData.find(p => p.id === athlete1);
        const athlete2Data = participantsData.find(p => p.id === athlete2);
        
        const athlete1Name = athlete1Data?.name || 'TBC';
        const athlete2Name = athlete2Data?.name || 'TBC';
        
        const athlete1Country = athlete1Data?.countryId 
          ? countriesData.find(c => c.id === athlete1Data.countryId)?.name 
          : 'Unknown';
        const athlete2Country = athlete2Data?.countryId 
          ? countriesData.find(c => c.id === athlete2Data.countryId)?.name 
          : 'Unknown';
        
        const athlete1Key = `${athlete1Name} (${athlete1Country})`;
        const athlete2Key = `${athlete2Name} (${athlete2Country})`;
        
        // Initialize stats
        if (!athleteStats[athlete1Key]) {
          athleteStats[athlete1Key] = { 
            id: athlete1,
            name: athlete1Name,
            country: athlete1Country,
            wins: 0, 
            matches: 0,
            setsWon: 0,
            setsLost: 0
          };
        }
        if (!athleteStats[athlete2Key]) {
          athleteStats[athlete2Key] = { 
            id: athlete2,
            name: athlete2Name,
            country: athlete2Country,
            wins: 0, 
            matches: 0,
            setsWon: 0,
            setsLost: 0
          };
        }
        
        // Count matches played
        athleteStats[athlete1Key].matches += 1;
        athleteStats[athlete2Key].matches += 1;
        
        // Process results - focus on wins
        if (fixture.result && fixture.result.winner) {
          if (fixture.result.winner === athlete1Name) {
            athleteStats[athlete1Key].wins += 1;
          } else if (fixture.result.winner === athlete2Name) {
            athleteStats[athlete2Key].wins += 1;
          }
        }
        
        // Process set scores for tie-breakers
        if (fixture.setScores && fixture.setScores.length > 0) {
          fixture.setScores.forEach(setScore => {
            const [score1, score2] = setScore.split('-').map(Number);
            if (!isNaN(score1)) athleteStats[athlete1Key].setsWon += score1;
            if (!isNaN(score2)) athleteStats[athlete2Key].setsWon += score2;
            if (!isNaN(score2)) athleteStats[athlete1Key].setsLost += score2;
            if (!isNaN(score1)) athleteStats[athlete2Key].setsLost += score1;
          });
        }
      });

      return Object.entries(athleteStats)
        .map(([_, stats]) => {
          const rankEntry = rankData.find(r => 
            r.country === `${stats.name} (${stats.country})` && 
            r.title === title && 
            r.category === category
          );
          return {
            id: stats.id,
            name: stats.name,
            country: `${stats.name} (${stats.country})`,
            wins: stats.wins,
            matches: stats.matches,
            setsWon: stats.setsWon,
            setsLost: stats.setsLost,
            setsDifference: stats.setsWon - stats.setsLost,
            rank: rankEntry?.rank || '0'
          };
        })
        .sort((a, b) => {
          // Primary sort by wins (descending)
          if (b.wins !== a.wins) return b.wins - a.wins;
          
          // Secondary sort by set difference (descending)
          if (b.setsDifference !== a.setsDifference) return b.setsDifference - a.setsDifference;
          
          // Tertiary sort by matches played (more matches first)
          return b.matches - a.matches;
        });
    }

    // Handle Team Sports (Football, Volleyball, etc.)
    // In the computeLeaderboard function, find the Basketball section (around line 200)
if (sportType === 'Team' || isFootball) {
  const countryStats = {};
  
  filteredFixtures.forEach(fixture => {
    if (!fixture.team1 || !fixture.team2) return;
    
    const team1Name = fixture.team1 === 'TBC' ? null : countriesData.find(c => c.id === fixture.team1)?.name;
    const team2Name = fixture.team2 === 'TBC' ? null : countriesData.find(c => c.id === fixture.team2)?.name;
    
    if (team1Name) {
      if (!countryStats[team1Name]) {
        countryStats[team1Name] = { points: 0, wins: 0, draws: 0, losses: 0, goalsFor: 0, goalsAgainst: 0 };
      }
    }
    
    if (team2Name) {
      if (!countryStats[team2Name]) {
        countryStats[team2Name] = { points: 0, wins: 0, draws: 0, losses: 0, goalsFor: 0, goalsAgainst: 0 };
      }
    }

    // Process results - MODIFIED FOR BASKETBALL SCORING
    if (fixture.result && fixture.result.winner) {
      const winner = fixture.result.winner;
      const isDraw = winner === 'Draw';
      
      if (team1Name && team2Name) {
        if (isDraw) {
          // Basketball doesn't typically have draws, but keeping this just in case
          countryStats[team1Name].points += 1;
          countryStats[team2Name].points += 1;
          countryStats[team1Name].draws += 1;
          countryStats[team2Name].draws += 1;
        } else if (winner === team1Name) {
          // Winner gets 2 points
          countryStats[team1Name].points += 2;
          countryStats[team1Name].wins += 1;
          // Loser gets 1 point
          countryStats[team2Name].points += 1;
          countryStats[team2Name].losses += 1;
        } else if (winner === team2Name) {
          // Winner gets 2 points
          countryStats[team2Name].points += 2;
          countryStats[team2Name].wins += 1;
          // Loser gets 1 point
          countryStats[team1Name].points += 1;
          countryStats[team1Name].losses += 1;
        }
      }
    }

    // Process scores for goal difference (or point difference in basketball)
    if (fixture.score && fixture.score.team1Score && fixture.score.team2Score) {
      const team1Score = parseInt(fixture.score.team1Score) || 0;
      const team2Score = parseInt(fixture.score.team2Score) || 0;
      
      if (team1Name) {
        countryStats[team1Name].goalsFor += team1Score;
        countryStats[team1Name].goalsAgainst += team2Score;
      }
      
      if (team2Name) {
        countryStats[team2Name].goalsFor += team2Score;
        countryStats[team2Name].goalsAgainst += team1Score;
      }
    }
  });

  return Object.entries(countryStats)
    .map(([country, stats]) => {
      const rankEntry = rankData.find(r => r.country === country && r.title === title && r.category === category);
      return {
        id: country,
        country,
        points: stats.points,
        wins: stats.wins,
        draws: stats.draws,
        losses: stats.losses,
        goalsFor: stats.goalsFor,
        goalsAgainst: stats.goalsAgainst,
        goalDifference: stats.goalsFor - stats.goalsAgainst,
        rank: rankEntry?.rank || '0'
      };
    })
    .sort((a, b) => {
      // Sort by points, then goal difference, then goals for
      if (b.points !== a.points) return b.points - a.points;
      if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
      return b.goalsFor - a.goalsFor;
    });
}

    // Handle Individual Sports (Athletics, Swimming, Weightlifting, etc.)
    if (sportType === 'Individual') {
      const athleteStats = {};
      
      filteredFixtures.forEach(fixture => {
        if (!fixture.athletes || fixture.athletes.length === 0) return;
        
        // Process head-to-head matches
        if (fixture.isHeadToHead && fixture.athletes.length >= 2) {
          const athlete1 = fixture.athletes[0];
          const athlete2 = fixture.athletes[1];
          
          const athlete1Data = participantsData.find(p => p.id === athlete1);
          const athlete2Data = participantsData.find(p => p.id === athlete2);
          
          const athlete1Name = athlete1Data?.name || 'TBC';
          const athlete2Name = athlete2Data?.name || 'TBC';
          
          const athlete1Country = athlete1Data?.countryId 
            ? countriesData.find(c => c.id === athlete1Data.countryId)?.name 
            : 'Unknown';
          const athlete2Country = athlete2Data?.countryId 
            ? countriesData.find(c => c.id === athlete2Data.countryId)?.name 
            : 'Unknown';
          
          const athlete1Key = `${athlete1Name} (${athlete1Country})`;
          const athlete2Key = `${athlete2Name} (${athlete2Country})`;
          
          // Initialize stats
          if (!athleteStats[athlete1Key]) {
            athleteStats[athlete1Key] = { 
              id: athlete1,
              scores: [], 
              bestScore: null,
              country: athlete1Country
            };
          }
          if (!athleteStats[athlete2Key]) {
            athleteStats[athlete2Key] = { 
              id: athlete2,
              scores: [], 
              bestScore: null,
              country: athlete2Country
            };
          }
          
          // Record scores
          if (fixture.score && fixture.score[athlete1]) {
            athleteStats[athlete1Key].scores.push(fixture.score[athlete1]);
          }
          if (fixture.score && fixture.score[athlete2]) {
            athleteStats[athlete2Key].scores.push(fixture.score[athlete2]);
          }
        } 
        // Process non-head-to-head events (relays, athletics, weightlifting)
        else {
          fixture.athletes.forEach(athleteId => {
            const athleteData = participantsData.find(p => p.id === athleteId);
            if (!athleteData) return;
            
            const athleteName = athleteData.name;
            const athleteCountry = athleteData.countryId 
              ? countriesData.find(c => c.id === athleteData.countryId)?.name 
              : 'Unknown';
            
            const athleteKey = `${athleteName} (${athleteCountry})`;
            
            if (!athleteStats[athleteKey]) {
              athleteStats[athleteKey] = { 
                id: athleteId,
                scores: [],
                bestScore: null,
                country: athleteCountry
              };
            }
            
            // Record scores for ranking
            if (fixture.score && fixture.score[athleteId]) {
              athleteStats[athleteKey].scores.push(fixture.score[athleteId]);
            }
          });
        }
      });

      // Calculate best scores
      Object.keys(athleteStats).forEach(key => {
        const stats = athleteStats[key];
        if (stats.scores.length > 0) {
          stats.bestScore = stats.scores.sort((a, b) => compareScores(a, b, resultType))[0];
        }
      });

      return Object.entries(athleteStats)
        .filter(([_, stats]) => stats.bestScore !== null)
        .map(([athleteKey, stats]) => {
          const rankEntry = rankData.find(r => 
            r.country === athleteKey && r.title === title && r.category === category
          );
          return {
            id: stats.id,
            country: athleteKey,
            bestScore: stats.bestScore,
            rank: rankEntry?.rank || '0'
          };
        })
        .sort((a, b) => {
          // Sort by performance (time/distance/weight)
          return compareScores(a.bestScore, b.bestScore, resultType);
        });
    }

    return [];
  };

  const computePoolStats = (fixtures, countriesData, participantsData, pool) => {
  // Initialize stats for all teams in the pool
  const teamStats = {};

  // First pass: Initialize all teams in the pool
  fixtures.forEach(fixture => {
    if (fixture.notes && fixture.notes.includes(pool)) {
      if (fixture.team1 && fixture.team1 !== 'TBC') {
        const teamName = countriesData.find(c => c.id === fixture.team1)?.name;
        if (teamName && !teamStats[teamName]) {
          teamStats[teamName] = {
            points: 0,
            wins: 0,
            losses: 0,
            goalsFor: 0,
            goalsAgainst: 0
          };
        }
      }
      if (fixture.team2 && fixture.team2 !== 'TBC') {
        const teamName = countriesData.find(c => c.id === fixture.team2)?.name;
        if (teamName && !teamStats[teamName]) {
          teamStats[teamName] = {
            points: 0,
            wins: 0,
            losses: 0,
            goalsFor: 0,
            goalsAgainst: 0
          };
        }
      }
    }
  });

  // Second pass: Process all matches in the pool
  fixtures.forEach(fixture => {
    if (fixture.notes && fixture.notes.includes(pool)) {
      const team1Name = fixture.team1 === 'TBC' ? null : countriesData.find(c => c.id === fixture.team1)?.name;
      const team2Name = fixture.team2 === 'TBC' ? null : countriesData.find(c => c.id === fixture.team2)?.name;

      // Skip invalid matches
      if (!team1Name || !team2Name) return;

      // Record goals/scoring
      if (fixture.score?.team1Score && fixture.score?.team2Score) {
        const team1Score = parseInt(fixture.score.team1Score) || 0;
        const team2Score = parseInt(fixture.score.team2Score) || 0;

        teamStats[team1Name].goalsFor += team1Score;
        teamStats[team1Name].goalsAgainst += team2Score;
        teamStats[team2Name].goalsFor += team2Score;
        teamStats[team2Name].goalsAgainst += team1Score;
      }

      // Process results
      if (fixture.result?.winner) {
        if (fixture.result.winner === 'Draw') {
          // Handle draws (though rare in basketball)
          teamStats[team1Name].points += 1;
          teamStats[team2Name].points += 1;
        } else if (fixture.result.winner === team1Name) {
          // Basketball scoring: 2 points for win, 1 for loss
          teamStats[team1Name].points += 2;
          teamStats[team1Name].wins += 1;
          teamStats[team2Name].points += 1;
          teamStats[team2Name].losses += 1;
        } else if (fixture.result.winner === team2Name) {
          teamStats[team2Name].points += 2;
          teamStats[team2Name].wins += 1;
          teamStats[team1Name].points += 1;
          teamStats[team1Name].losses += 1;
        }
      }
    }
  });

  // Convert to array and sort
  return Object.entries(teamStats)
    .map(([country, stats]) => ({
      id: country,
      country,
      points: stats.points,
      wins: stats.wins,
      losses: stats.losses,
      goalsFor: stats.goalsFor,
      goalsAgainst: stats.goalsAgainst,
      goalDifference: stats.goalsFor - stats.goalsAgainst
    }))
    .sort((a, b) => {
      // Primary sort by points (descending)
      if (b.points !== a.points) return b.points - a.points;
      
      // Secondary sort by wins (descending)
      if (b.wins !== a.wins) return b.wins - a.wins;
      
      // Tertiary sort by goal difference (descending)
      if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
      
      // Final sort by goals scored (descending)
      return b.goalsFor - a.goalsFor;
    });
};

  // Helper function to check if a category has pools
  const hasPoolsForCategory = (fixturesData, category) => {
    return fixturesData.some(fixture => 
      fixture.category === category && 
      fixture.notes && 
      fixture.notes.match(/Pool (A|B)/)
    );
  };

  useEffect(() => {
    const fetchSportDetails = async () => {
      try {
        setLoading(true);

        if (!sportId) {
          setError('Invalid sport ID');
          return;
        }

        const sportDoc = await getDoc(doc(db, 'sports', sportId));
        if (!sportDoc.exists()) {
          setError('Sport not found');
          return;
        }
        const sportData = sportDoc.data();
        setSportName(sportData.name || 'Unknown Sport');
        setSportType(sportData.type || 'Team');

        const countriesSnapshot = await getDocs(collection(db, 'countries'));
        const countriesData = countriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));

        const participantsSnapshot = await getDocs(collection(db, 'participants'));
        const participantsDataLocal = participantsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          countryId: doc.data().countryId
        }));
        setParticipantsData(participantsDataLocal);

        const fixturesSnapshot = await getDocs(query(collection(db, 'fixtures'), where('sportId', '==', sportId)));
        const fixturesData = fixturesSnapshot.docs.map(doc => {
          const data = doc.data();
          const competitors = computeCompetitors(data, countriesData, participantsDataLocal);
          return {
            id: doc.id,
            ...data,
            competitors
          };
        });

        console.log('Fetched fixtures:', fixturesData);

        const poolMap = { 'Pool A': new Set(), 'Pool B': new Set() };
        fixturesData.forEach(fixture => {
          const poolMatch = fixture.notes && fixture.notes.match(/Pool (A|B)/);
          if (poolMatch) {
            const pool = `Pool ${poolMatch[1]}`;
            if (sportData.type === 'Team') {
              if (fixture.team1 && fixture.team1 !== 'TBC') {
                const country = countriesData.find(c => c.id === fixture.team1)?.name;
                if (country) poolMap[pool].add(country);
              }
              if (fixture.team2 && fixture.team2 !== 'TBC') {
                const country = countriesData.find(c => c.id === fixture.team2)?.name;
                if (country) poolMap[pool].add(country);
              }
            } else if (sportData.type === 'Individual') {
              fixture.athletes?.forEach(athleteId => {
                if (athleteId && athleteId !== 'TBC') {
                  const athlete = participantsDataLocal.find(p => p.id === athleteId);
                  if (athlete?.countryId) {
                    const country = countriesData.find(c => c.id === athlete.countryId)?.name;
                    if (country) poolMap[pool].add(country);
                  }
                }
              });
            }
          }
        });

        setPools({
          'Pool A': Array.from(poolMap['Pool A']).sort(),
          'Pool B': Array.from(poolMap['Pool B']).sort()
        });

        setPoolStats({
          'Pool A': computePoolStats(fixturesData, countriesData, participantsDataLocal, 'Pool A'),
          'Pool B': computePoolStats(fixturesData, countriesData, participantsDataLocal, 'Pool B')
        });

        const currentDateTime = new Date('2025-08-03T08:49:00+04:00'); // Updated to 08:49 AM +04, August 03, 2025
        const upcoming = [];
        const completed = [];
        fixturesData.forEach(fixture => {
          const fixtureDateTimeStr = `${fixture.date}T${fixture.time || '23:59'}+04:00`;
          const fixtureDateTime = new Date(fixtureDateTimeStr);
          if (isNaN(fixtureDateTime.getTime())) {
            console.warn(`Invalid date/time for fixture ${fixture.id}: ${fixtureDateTimeStr}`);
            return;
          }

          const fixtureItem = {
            id: fixture.id,
            sport: sportData.name,
            teams: fixture.competitors,
            event: fixture.type === 'Individual' && !fixture.isHeadToHead ? fixture.title : null,
            date: fixture.date,
            notes: fixture.notes,
            time: fixture.time || 'TBA',
            venue: fixture.venue,
            result: fixture.result || {},
            score: fixture.score || {},
            setScores: fixture.setScores || [],
            type: fixture.type,
            isHeadToHead: fixture.isHeadToHead,
            athletes: fixture.athletes || [],
            category: fixture.category
          };

          console.log(fixtureItem);
          const hasValidResult =
            fixture.result &&
            Object.keys(fixture.result).length > 0 &&
            Object.values(fixture.result).some(r => r && r !== 'None' && r !== '');

          const hasValidScore =
            fixture.score &&
            Object.keys(fixture.score).length > 0 &&
            Object.values(fixture.score).some(s => s && s !== '' && s !== 'None' && !isNaN(parseFloat(s)));

          console.log(`Fixture ${fixture.id}:`, {
            date: fixture.date,
            time: fixture.time,
            title: fixture.title,
            notes: fixture.notes,
            result: fixture.result,
            score: fixture.score,
            hasValidResult,
            hasValidScore,
            isFuture: fixtureDateTime >= currentDateTime,
            category: fixture.category
          });

          if (hasValidResult && hasValidScore && fixture.competitors !== 'TBC' && (!fixtureItem.event || fixtureItem.event !== 'TBC')) {
            completed.push(fixtureItem);
          } else {
            upcoming.push(fixtureItem);
          }
        });

        const sortedUpcoming = upcoming.sort(
          (a, b) => new Date(`${a.date}T${a.time || '00:00'}+04:00`) - new Date(`${b.date}T${b.time || '00:00'}+04:00`)
        );
        const sortedResults = completed.sort(
          (a, b) => new Date(`${a.date}T${a.time || '00:00'}+04:00`) - new Date(`${b.date}T${b.time || '00:00'}+04:00`)
        );

        console.log('Upcoming fixtures:', sortedUpcoming);
        console.log('Filtered and sorted results:', sortedResults);

        setFixtures(sortedUpcoming);
        setResults(sortedResults);

        const ranksSnapshot = await getDocs(query(collection(db, 'leaderboard_main'), where('sportId', '==', sportId)));
        const rankData = ranksSnapshot.docs.map(doc => doc.data());

        const titleCategoryPairs = [...new Set(fixturesData.map(f => `${f.title}|${f.category}`))];
        const scoreLeaderboards = titleCategoryPairs.reduce((acc, pair) => {
          const [title, category] = pair.split('|');
          const filtered = fixturesData.filter(f => f.title === title && f.category === category);
          acc[`${title}|${category}`] = computeLeaderboard(filtered, countriesData, participantsDataLocal, title, category, rankData);
          return acc;
        }, {});

        const medalLeaderboards = {};
        rankData.forEach(rank => {
          const { category, country, rank: rankValue } = rank;
          if (!medalLeaderboards[category]) {
            medalLeaderboards[category] = {};
            countriesData.forEach(country => {
              medalLeaderboards[category][country.name] = { gold: 0, silver: 0, bronze: 0 };
            });
          }
          if (medalLeaderboards[category][country]) {
            if (rankValue === '1') {
              medalLeaderboards[category][country].gold += 1;
            } else if (rankValue === '2') {
              medalLeaderboards[category][country].silver += 1;
            } else if (rankValue === '3') {
              medalLeaderboards[category][country].bronze += 1;
            }
          }
        });

        const formattedMedalLeaderboards = Object.entries(medalLeaderboards).reduce((acc, [category, medals]) => {
          acc[category] = Object.entries(medals)
            .map(([country, m], index) => ({
              id: index + 1,
              country,
              gold: m.gold,
              silver: m.silver,
              bronze: m.bronze,
              total: m.gold + m.silver + m.bronze
            }))
            .filter(row => row.gold > 0 || row.silver > 0 || row.bronze > 0)
            .sort((a, b) => b.gold - a.gold || b.silver - a.silver || b.bronze - a.bronze || a.country.localeCompare(b.country));
          return acc;
        }, {});

        setLeaderboards({ score: scoreLeaderboards, medals: formattedMedalLeaderboards });
        setError('');
      } catch (err) {
        console.error('Error fetching sport details:', err);
        setError(err.message || 'Failed to load sport details');
      } finally {
        setLoading(false);
      }
    };
    fetchSportDetails();
  }, [sportId, sportName]);

  const filteredFixtures = fixtures.filter(fixture => {
    const search = searchQuery.toLowerCase();
    return (
      (fixture.teams && fixture.teams.toLowerCase().includes(search)) ||
      (fixture.event && fixture.event.toLowerCase().includes(search)) ||
      (fixture.venue && fixture.venue.toLowerCase().includes(search))
    );
  });

  const showPoolsTab = pools['Pool A'].length > 0 || pools['Pool B'].length > 0;

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.5); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
          .in-progress-dot {
            width: 10px;
            height: 10px;
            background-color: #FFD700;
            border-radius: 50%;
            animation: pulse 1.5s infinite ease-in-out;
          }
        `}
      </style>
      {loading ? (
        <Box sx={{ mt: 10, mx: 1 }}>
          <LoadingScreen sx={{ mt: 30 }} />
        </Box>
      ) : (
        
        <Box sx={{ background: 'linear-gradient(159deg, rgba(0,71,171,1) 0%, rgba(28,169,201,1) 100%)' }}>
          {error ? (
            <Typography variant="h6" sx={{ color: 'red', textAlign: 'center' }}>
              {error}
            </Typography>
          ) : (
            <>
              <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mx: 2, mt: 3 }}>
                {translatedNames[sportName] || sportName} <span style={{ fontSize: 30 }}>{sportIcons[sportName]}</span>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mb: 3,
                  mt: 3,
                  justifyContent: 'center',
                  alignItems: { xs: 'center', sm: 'flex-start' }
                }}
              >
                <Chip
                  label="Rencontres"
                  icon={<ListAltIcon style={{ color: 'white' }} />}
                  onClick={() => setSelectedTab('upcoming')}
                  sx={{
                    fontSize: '1.1rem',
                    padding: '12px 24px',
                    background: selectedTab === 'upcoming'
                      ? 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)'
                      : 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: selectedTab === 'upcoming' ? 'bold' : 'normal',
                    '&:hover': {
                      background: 'linear-gradient(159deg, rgba(0,127,255,0.8) 0%, rgba(42,82,190,0.8) 100%)'
                    },
                    borderRadius: '16px',
                    minWidth: '200px'
                  }}
                />
                {sportType !== 'Cultural' && (
                  <>
                    <Chip
                      label="Résultats"
                      icon={<EmojiEventsIcon style={{ color: 'white' }} />}
                      onClick={() => setSelectedTab('results')}
                      sx={{
                        fontSize: '1.1rem',
                        padding: '12px 24px',
                        background: selectedTab === 'results'
                          ? 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)'
                          : 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: selectedTab === 'results' ? 'bold' : 'normal',
                        '&:hover': {
                          background: 'linear-gradient(159deg, rgba(0,127,255,0.8) 0%, rgba(42,82,190,0.8) 100%)'
                        },
                        borderRadius: '16px',
                        minWidth: '200px'
                      }}
                    />
                    {sportName.toLowerCase() !== 'badminton' && (
            <Chip
              label="Classements"
              icon={<LeaderboardIcon style={{ color: 'white' }} />}
              onClick={() => setSelectedTab('leaderboards')}
              sx={{
                fontSize: '1.1rem',
                padding: '12px 24px',
                background: selectedTab === 'leaderboards'
                  ? 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)'
                  : 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: selectedTab === 'leaderboards' ? 'bold' : 'normal',
                '&:hover': {
                  background: 'linear-gradient(159deg, rgba(0,127,255,0.8) 0%, rgba(42,82,190,0.8) 100%)'
                },
                borderRadius: '16px',
                minWidth: '200px'
              }}
            />
          )}
        </>
      )}
                {showPoolsTab && (
                  <Chip
                    label="Pools"
                    icon={<GroupIcon style={{ color: 'white' }} />}
                    onClick={() => setSelectedTab('pools')}
                    sx={{
                      fontSize: '1.1rem',
                      padding: '12px 24px',
                      background: selectedTab === 'pools'
                        ? 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)'
                        : 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: selectedTab === 'pools' ? 'bold' : 'normal',
                      '&:hover': {
                        background: 'linear-gradient(159deg, rgba(0,127,255,0.8) 0%, rgba(42,82,190,0.8) 100%)'
                      },
                      borderRadius: '16px',
                      minWidth: '200px'
                    }}
                  />
                )}
              </Box>
              {selectedTab === 'upcoming' && (
                <>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                      py: 3,
                      px: { xs: 2, sm: 4 },
                      boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: 'black',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ListAltIcon sx={{ fontSize: 35, color: 'rgba(28,169,201,1)' }} />
                      Rencontres {translatedNames[sportName] || sportName}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Rechercher une rencontre (participants, événement, lieu)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ my: 2, backgroundColor: 'white', borderRadius: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {filteredFixtures.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'black', mt: 2 }}>
                        Aucune rencontre à venir disponible.
                      </Typography>
                    ) : (
                      <Grid container spacing={2} mt={1}>
                        {filteredFixtures.map((fixture) => {
                          // Determine if the fixture has valid scores but no winner
                          const hasValidScore = 
                            (fixture.type === 'Team' && 
                             fixture.score && 
                             fixture.score.team1Score != null && 
                             fixture.score.team2Score != null && 
                             fixture.score.team1Score !== '' && 
                             fixture.score.team2Score !== '') ||
                            (fixture.type === 'Individual' && 
                             fixture.score && 
                             Object.keys(fixture.score).length > 0 && 
                             Object.values(fixture.score).some(s => s && s !== '' && s !== 'None' && !isNaN(parseFloat(s))));
                          const hasWinner = 
                            fixture.result && 
                            fixture.result.winner && 
                            fixture.result.winner !== '' && 
                            fixture.result.winner !== 'None';
                          const showInProgress = hasValidScore && !hasWinner;

                          return (
                            <Grid sx={{ mx: 'auto' }} item xs={12} sm={6} md={4} key={`${fixture.sport}-${fixture.id}`}>
                              <Card
                                elevation={3}
                                sx={{
                                  color: 'white',
                                  borderRadius: 2,
                                  height: '100%',
                                  minWidth: '330px',
                                  backgroundColor: 'rgb(0,127,255)',
                                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
                                  transition: 'transform 0.2s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.02)',
                                  },
                                }}
                              >
                                <CardContent sx={{ position: 'relative', p: 3 }}>
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <CalendarTodayIcon fontSize="small" sx={{ color: 'white' }} />
                                    <Typography sx={{ mr: 5 }} variant="subtitle2" color="white">
                                      {fixture.date}
                                    </Typography>
                                    <AccessTimeIcon fontSize="small" sx={{ color: 'white' }} />
                                    <Typography variant="body2">{fixture.time}</Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                      {sportType === 'Cultural' && <b>{fixture.teams}</b>}
                                      {sportType === 'Team' && <b>{fixture.teams}</b>}
                                      {sportType === 'Individual' && <b>{fixture.event}</b>}
                                    </Typography>
                                  </Box>
                                  {sportType === 'Team' && 
                                   fixture.score && 
                                   fixture.score.team1Score != null && 
                                   fixture.score.team2Score != null && 
                                   fixture.score.team1Score !== '' && 
                                   fixture.score.team2Score !== '' && (
                                    <Box sx={{ mx: 'auto', textAlign: 'center' }} alignItems="center" gap={1} mb={1}>
                                      <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center', background: 'white', borderRadius: '10px', color: 'darkblue', mx: 8 }}>
                                        {`${fixture.score.team1Score}-${fixture.score.team2Score}`}
                                        {sportName === 'Volleyball' && fixture.setScores.length > 0 ? `, Sets: ${fixture.setScores.join(', ')}` : ''}
                                      </Typography>
                                    </Box>
                                  )}
                                  {sportType === 'Individual' && 
                                   fixture.score && 
                                   Object.keys(fixture.score).length > 0 && 
                                   Object.values(fixture.score).some(s => s && s !== '' && s !== 'None' && !isNaN(parseFloat(s))) && (
                                    <Box sx={{ mx: 'auto', textAlign: 'center' }} alignItems="center" gap={1} mb={1}>
                                      <Typography variant="h6" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                        {Object.entries(fixture.score)
                                          .map(([id, score]) => score && score !== '' && score !== 'None' ? `${participantsData.find(p => p.id === id)?.name || id}: ${score}` : null)
                                          .filter(s => s)
                                          .join(', ')}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box display="flex" sx={{ mb: 1 }} alignItems="center" gap={1}>
                                    <Typography variant="body1"><b>{fixture.notes}</b></Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">📍 {fixture.venue}</Typography>
                                  </Box>
                                  {fixture.category && (
                                    <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                                      {fixture.category === 'Boys' && (
                                        <BoyIcon sx={{ color: 'white', fontSize: 24 }} />
                                      )}
                                      {fixture.category === 'Girls' && (
                                        <GirlIcon sx={{ color: 'white', fontSize: 24 }} />
                                      )}
                                    </Box>
                                  )}
                                  {showInProgress && (
                                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                                      <div className="in-progress-dot" />
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                              <hr style={{ marginTop: '10px', marginBottom: '10px' }} />
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                  <br />
                  <br />
                </>
              )}
              {selectedTab === 'results' && (
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    pb: 3,
                    pt: 1,
                    minHeight: 800,
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ color: 'black', fontWeight: '550', mt: 3, mx: 2, mb: 2 }}>
                    <EmojiEventsIcon sx={{ fontSize: 30, color: 'rgb(0,127,255)' }} /> Résultats
                  </Typography>
                  {results.length === 0 ? (
                    <>
                      <Typography variant="body2" sx={{ color: 'black', mb: 2, mx: 2 }}>
                        Aucun résultat disponible pour {translatedNames[sportName] || sportName}.
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                      <Grid
                        spacing={2}
                        justifyContent="center"
                        alignItems="stretch"
                      >
                        {results.map(fixture => (
                          <Grid item xs={12} sm={6} md={4} key={`${fixture.sport}-${fixture.id}`}>
                            <Card
                              elevation={3}
                              sx={{
                                mx: 1,
                                mb:2,
                                borderRadius: 2,
                                height: '100%',
                                background: 'rgb(0,127,255)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                                },
                              }}
                            >
                              <CardContent sx={{ position: 'relative', p: 2 }}>
                                <Box sx={{ borderRadius: '8px 8px 0 0', p: 2, mb: 2, color: 'white', textAlign: 'center' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {fixture.teams || fixture.event}
                                  </Typography>
                                  {(fixture.type === 'Team' && fixture.score.team1Score != null && fixture.score.team2Score != null) || 
                                   (fixture.type === 'Individual' && Object.keys(fixture.score).length > 0) ? (
                                    <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', background: 'white', borderRadius: '10px', color: 'darkblue', mx: 8, mb: 1 }}>
                                      {fixture.type === 'Team'
                                        ? `${fixture.score.team1Score}-${fixture.score.team2Score}`
                                        : Object.entries(fixture.score)
                                            .map(([id, score]) => score ? `${participantsData.find(p => p.id === id)?.name || id}: ${score}` : null)
                                            .filter(s => s)
                                            .join(', ')}
                                      {sportName === 'Volleyball' && fixture.setScores.length > 0 ? `, Sets: ${fixture.setScores.join(', ')}` : ''}
                                    </Typography>
                                  ) : null}
                                  {fixture.type === 'Team' && fixture.result && fixture.result.winner && fixture.result.winner !== '' && fixture.result.winner !== 'None' && (
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'white', mt: 1 }}>
                                      Vainqueur: {fixture.result.winner}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CalendarTodayIcon fontSize="small" sx={{ color: 'white' }} />
                                  <Typography variant="body2" color="white">{fixture.date}</Typography>
                                  <AccessTimeIcon fontSize="small" sx={{ color: 'white' }} />
                                  <Typography variant="body2" color="white">{fixture.time}</Typography>
                                </Box>
                                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" color="white">📍 {fixture.venue}</Typography>
                                </Box>
                                {fixture.category && (
                                  <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                                    {fixture.category === 'Boys' && <BoyIcon sx={{ color: 'white', fontSize: 30 }} />}
                                    {fixture.category === 'Girls' && <GirlIcon sx={{ color: 'white', fontSize: 30 }} />}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  <br />
                  <br />
                </Box>
              )}
              {selectedTab === 'leaderboards' && (
  <>
    <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mt: 3, mx: 2 }}>
      <LeaderboardIcon sx={{ fontSize: 30 }} /> Classement
    </Typography>
    {Object.keys(leaderboards.score).length === 0 && Object.keys(leaderboards.medals).length === 0 ? (
      <Typography variant="body2" sx={{ color: 'white', mb: 2, mx: 2 }}>
        Aucun classement disponible.
      </Typography>
    ) : (
      <>
        {Object.entries(leaderboards.score).map(([key, leaderboard]) => {
          const [title, category] = key.split('|');
          const isBadminton = sportName.toLowerCase() === 'badminton' || title.toLowerCase().includes('badminton');
          const isFootball = title.toLowerCase().includes('football');
          const isTeam = leaderboard[0]?.points !== undefined && !isBadminton;

          return (
            <Box key={key} sx={{ mb: 4, mx: 2 }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                Classement {title} - {category}
              </Typography>
              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '8px',
                  overflow: 'auto',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                }}
              >
                <Table aria-label={`Classement pour ${title} - ${category}`}>
                  <TableHead sx={{ 
                    backgroundColor: 'rgb(0,127,255)',
                    backgroundImage: 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)'
                  }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rang</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        {isTeam ? 'Pays' : 'Participant'}
                      </TableCell>
                      {isBadminton ? (
                        <>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Victoires</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Matchs</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sets Gagnés</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sets Perdus</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Diff. Sets</TableCell>
                        </>
                      ) : isTeam ? (
                        <>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Points</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Victoires</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nuls</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Défaites</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Buts Pour</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Buts Contre</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Diff. Buts</TableCell>
                        </>
                      ) : (
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Meilleure Performance</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboard.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isBadminton ? 7 : isTeam ? (isFootball ? 8 : 5) : 3} sx={{ color: 'black', textAlign: 'center' }}>
                          Aucun match terminé pour cet événement.
                        </TableCell>
                      </TableRow>
                    ) : (
                      leaderboard.map((row, index) => (
                        <TableRow
                          key={row.id || index}
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
                          }}
                        >
                          <TableCell sx={{ color: 'black', fontWeight: row.rank <= 3 && row.rank !== '0' ? 'bold' : 'normal' }}>
                            {row.rank === '0' ? '-' : (
                              <>
                                {row.rank}
                                {row.rank === '1' && ' 🥇'}
                                {row.rank === '2' && ' 🥈'}
                                {row.rank === '3' && ' 🥉'}
                              </>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'black' }}>{row.country}</TableCell>
                          {isBadminton ? (
                            <>
                              <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>{row.wins}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.matches}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.setsWon}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.setsLost}</TableCell>
                              <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                                {row.setsDifference}
                              </TableCell>
                            </>
                          ) : isTeam ? (
                            <>
                              <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>{row.points}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.wins}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.draws}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.losses}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.goalsFor}</TableCell>
                              <TableCell sx={{ color: 'black' }}>{row.goalsAgainst}</TableCell>
                              <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>
                                {row.goalDifference > 0 ? `+${row.goalDifference}` : row.goalDifference}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell sx={{ color: 'black' }}>{row.bestScore || 'N/A'}</TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}

        {/* Medals table remains the same */}
        {Object.entries(leaderboards.medals).map(([category, leaderboard]) => (
          <Box key={category} sx={{ mb: 4, mx: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              Médailles {category}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '8px',
                overflow: 'auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
            >
              <Table>
                <TableHead sx={{ 
                  backgroundColor: 'rgb(0,127,255)',
                  backgroundImage: 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)'
                }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pays</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>🥇</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>🥈</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>🥉</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaderboard.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
                      }}
                    >
                      <TableCell sx={{ color: 'black' }}>{row.country}</TableCell>
                      <TableCell sx={{ color: '#FFD700', fontWeight: 'bold' }}>{row.gold}</TableCell>
                      <TableCell sx={{ color: '#C0C0C0', fontWeight: 'bold' }}>{row.silver}</TableCell>
                      <TableCell sx={{ color: '#CD7F32', fontWeight: 'bold' }}>{row.bronze}</TableCell>
                      <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>{row.total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </>
    )}
  </>
)}
              {selectedTab === 'pools' && (
                <>
                  <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mt: 3, mx: 2 }}>
                    <GroupIcon sx={{ fontSize: 30 }} /> Pools
                  </Typography>
                  {pools['Pool A'].length === 0 && pools['Pool B'].length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'white', mb: 2, mx: 2 }}>
                      Aucune information sur les poules disponible.
                    </Typography>
                  ) : (
                    <Box sx={{ background: 'white', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', mx: 2 }}>
                      <TableContainer
                        component={Paper}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          backdropFilter: 'blur(20px)',
                          borderRadius: '8px',
                          overflow: 'auto',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        }}
                      >
                        <Table>
                          <TableHead sx={{ backgroundColor: 'rgb(0,127,255)', backgroundImage: 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)' }}>
                            <TableRow sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Poule</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pays</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Points</TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Victoires</TableCell>
                              {sportName === 'Football' && (
                                <>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nuls</TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Défaites</TableCell>
                                </>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(poolStats).map(([pool, stats]) => (
                              stats.length > 0 && (
                                <TableRow
                                  key={pool}
                                  sx={{
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                    transition: '0.2s ease',
                                  }}
                                >
                                  <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>{pool}</TableCell>
                                  <TableCell sx={{ color: 'black' }}>
                                    {stats.map((row, index) => (
                                      <Box key={row.country} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        {row.country}
                                      </Box>
                                    ))}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }}>
                                    {stats.map((row, index) => (
                                      <Box key={row.country} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        {row.points}
                                      </Box>
                                    ))}
                                  </TableCell>
                                  <TableCell sx={{ color: 'black' }}>
                                    {stats.map((row, index) => (
                                      <Box key={row.country} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        {row.wins}
                                      </Box>
                                    ))}
                                  </TableCell>
                                  {sportName === 'Football' && (
                                    <>
                                      <TableCell sx={{ color: 'black' }}>
                                        {stats.map((row, index) => (
                                          <Box key={row.country} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            {row.draws}
                                          </Box>
                                        ))}
                                      </TableCell>
                                      <TableCell sx={{ color: 'black' }}>
                                        {stats.map((row, index) => (
                                          <Box key={row.country} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            {row.losses}
                                          </Box>
                                        ))}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              )
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
          <br />
          <br />
        </Box>
      )}
    </>
  );
};

export default SportDetails;